// Guardados — everything the reader flagged (ios/Sources/Views/SavedMessagesSheet.swift). Saving is
// a PREFERENCE (`flagged_post`, shared/API.md §24), never a post field. The store answers on the
// first frame; the open REPLACES from the server behind it (an unsave elsewhere leaves no trace in
// the response). A row jumps to the message; its trailing button unsaves in place.
import { placeIcon } from './PlaceLabel';
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { t } from '../../../i18n/i18n';
import { Icon } from '../../../design/icons';
import { DestinationFrame, EmptyState, InlineStatus } from '../../kit/Kit';
import { PostResultRow } from '../../kit/PostResultRow';
import { useLive, useMinuteClock } from '../../hooks';
import { useShell } from '../../shell/ShellContext';
import { buildResultRows } from './destinationsModel';
import './destinations.css';

export function SavedView() {
  const shell = useShell();
  const now = useMinuteClock();
  const { value: snapshot } = useLive(() => data.call('savedSnapshot'), ['savedPosts', 'posts', 'users', 'channels', 'channelParticipants', 'syncState'], []);
  const [refresh, setRefresh] = useState<'loading' | 'idle' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setRefresh('loading');
    data.call('refreshSavedPosts').then(
      (ok) => alive && setRefresh(ok ? 'idle' : 'failed'),
      () => alive && setRefresh('failed'),
    );
    return () => {
      alive = false;
    };
  }, [attempt]);

  // Rows unsaved on this screen leave ON the click; the store write (and a refusal's rollback)
  // follows behind.
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const rows = useMemo(() => (snapshot ? buildResultRows(snapshot, now).filter((r) => !leaving.has(r.postId)) : []), [snapshot, now, leaving]);
  // Once the store agrees (the row is gone from it), forget it — a later save elsewhere shows again.
  useEffect(() => {
    if (!snapshot || !leaving.size) return;
    const present = new Set(snapshot.posts.map((p) => p.id));
    if ([...leaving].some((id) => !present.has(id))) setLeaving((s) => new Set([...s].filter((id) => present.has(id))));
  }, [snapshot, leaving]);

  const unsave = (postId: string) => {
    setLeaving((s) => new Set(s).add(postId));
    const restore = () => {
      setLeaving((s) => {
        const next = new Set(s);
        next.delete(postId);
        return next;
      });
      shell.toast(t('web.destinations.saved.unsaveFailed'), 'error');
    };
    data.call('setPostSaved', postId, false).then((ok) => !ok && restore(), restore);
  };

  const failed = refresh === 'failed' ? <InlineStatus kind="error" text={t('web.destinations.saved.refreshFailed')} onRetry={() => setAttempt((n) => n + 1)} /> : null;
  let body;
  if (!rows.length) {
    const neverFetched = !snapshot || snapshot.fetchedAt === 0;
    if (neverFetched && refresh === 'loading') body = <InlineStatus kind="loading" text={t('web.destinations.loading')} />;
    else
      body = (
        <>
          {failed}
          <EmptyState title={t('channel.saved.empty')} />
        </>
      );
  } else {
    body = (
      <>
        {failed}
        <div className="dest-results">
          {rows.map((row) => (
            <PostResultRow
              key={row.postId}
              userId={row.userId}
              authorName={row.authorName}
              authorVersion={row.authorVersion}
              place={row.place}
              placeIsPrivate={row.placeType === 'P'}
              placeIcon={placeIcon(row.placeType)}
              time={row.timeLabel}
              snippet={row.text}
              onOpen={() => shell.jumpToPost(row.postId)}
              trailing={
                <button className="icon-button dest-unsave" title={t('channel.unsave')} aria-label={t('channel.unsave')} onClick={() => unsave(row.postId)}>
                  <Icon name="bookmark" size={16} />
                </button>
              }
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <DestinationFrame icon="bookmark" title={t('channel.saved.title')}>
      {body}
    </DestinationFrame>
  );
}
